/**
 * The pure half of the proposal editor: seeding, dirty-tracking, the payload
 * builder and the adopt-vs-preserve decision. No React — `useProposalEditor`
 * is what wires these into state, and each one is unit-tested on its own.
 */
import type { ProposalRow } from './proposalViewModel'

/**
 * Seeds a productId→quantity Map from server rows.
 * Call this on first mount and when "Descartar" is clicked.
 */
export const seedLocalQuantities = (rows: Array<ProposalRow>): Map<number, number> =>
  new Map(rows.map((row) => [row.product.id, row.quantity]))

/**
 * Returns true when any local quantity differs from the corresponding server quantity.
 * Rows with no local entry (Map miss) are treated as matching server (not dirty).
 */
export const computeDirty = (
  rows: Array<ProposalRow>,
  localQuantities: Map<number, number>,
): boolean =>
  rows.some((row) => {
    const local = localQuantities.get(row.product.id)
    return local !== undefined && local !== row.quantity
  })

/**
 * True when saving, with nothing edited, would CHANGE the stored order.
 *
 * That is the question the screen's notice asks, and it is not the same as "do
 * the rows match what is stored". A row reads at the store's floor, so a carta
 * product the order never named (the carta gained it after the proposal was
 * generated) or a stored zero under a positive floor shows a quantity the order
 * does not have — and `computeDirty` cannot see it, because that one compares
 * the local edits against these same floored rows and reports "no changes" with
 * no user edit. The store would review a plate it never ordered, find nothing
 * to correct, and leave.
 *
 * ⚠️ But a row differing from storage does NOT mean a save would change
 * anything, and asking the wrong one of those two questions produced a real
 * false alarm: a store whose floor is 0 that saves every line at zero ends with
 * an order carrying NO lines, its rows all read 0, and every row then "differs"
 * from an absent line — while a save would send the same empty payload again.
 * So the comparison runs against what `buildLines` WOULD send, seeded from the
 * server rows rather than the user's edits (those are `dirty`'s business), and
 * the two cannot drift because it is the same function.
 *
 * It is deliberately NOT folded into `dirty`: dirty arms the unsaved-changes
 * guard, and warning about unsaved work the user never did would be a second
 * lie.
 */
export const divergesFromStored = (
  rows: Array<ProposalRow>,
  order: {products: Array<{productId: number; quantity: number}>} | null,
  menuProductIds: Set<number>,
  minQuantityPerProduct: number,
): boolean => {
  if (order === null) return false
  const wouldSend = buildLines(rows, new Map(), menuProductIds, minQuantityPerProduct)
  if (wouldSend.length !== order.products.length) return true
  const storedByProduct = new Map(order.products.map((line) => [line.productId, line.quantity]))
  return wouldSend.some((line) => storedByProduct.get(line.productId) !== line.quantity)
}

/**
 * Builds the replace-all lines array to send to orders.editLines.
 * Filters out any productId not in menuProductIds (client-side guard; should
 * not happen from UI but defends against stale state).
 *
 * ONE rule, and it is the model's: a line of this store carries a quantity in
 * [minQuantityPerProduct, MAX], so every carta row travels floored at the
 * minimum and NOTHING is dropped. There is no state below the floor to
 * represent — above zero "not ordering a product" does not exist for the store
 * — so a stored zero (a staff correction, or history) and a carta product the
 * order never named (the carta gained it after the proposal was generated) both
 * go out at the floor. Dropping either would remove a line from an order the
 * store never asked to shrink, silently, on a save about some other product,
 * and the save is a replace-all.
 *
 * The rows already read at the floor (mergeProposalViewModel), so this repeats
 * the clamp rather than introducing it: what the screen shows is what travels.
 *
 * With a floor of 0 the clamp is the identity and the zeros DO drop out: for
 * that store a zero is "no pedido", and an order with no lines is the opt-out
 * (`isOptedOut`). Writing a zero per carta product instead would turn every
 * absence into a stored zero and fill the trail with `none→0` for products
 * nobody touched.
 */
export const buildLines = (
  rows: Array<ProposalRow>,
  localQuantities: Map<number, number>,
  menuProductIds: Set<number>,
  minQuantityPerProduct: number,
): Array<{productId: number; quantity: number}> =>
  rows
    .filter((row) => menuProductIds.has(row.product.id))
    .map((row) => ({
      productId: row.product.id,
      quantity: Math.max(minQuantityPerProduct, localQuantities.get(row.product.id) ?? row.quantity),
    }))
    // Above a zero floor this drops nothing — every quantity is at least the
    // floor, so the payload stays complete. It only bites with a floor of 0,
    // where it keeps ABSENT and ZERO apart: a row nobody ordered stays off the
    // order instead of becoming a stored zero, which is what `line-diff` exists
    // to protect and what the trail would otherwise fill with `none→0`.
    .filter((line) => line.quantity > 0)

/**
 * Decides whether the seeding effect should adopt new server data or preserve
 * local edits. The decision is based on whether the USER has edited —
 * i.e. local vs the LAST-RECONCILED BASELINE (what we last seeded them),
 * NOT local vs the fresh server rows.
 *
 * Returns true  → adopt new server data (user has no unsaved edits vs baseline).
 * Returns false → preserve local edits (user has unsaved edits; raise notice instead).
 *
 * @param baseline  The Map that was last seeded into local state (the reconciliation point).
 * @param local     The current local edit state.
 */
export const shouldAdoptServer = (
  baseline: Map<number, number>,
  local: Map<number, number>,
): boolean => {
  // If any local entry differs from the baseline the user seeded from, they have
  // unsaved edits → do NOT adopt.
  for (const [productId, baselineQty] of baseline) {
    const localQty = local.get(productId)
    if (localQty !== undefined && localQty !== baselineQty) return false
  }
  return true
}
